import React, { useMemo } from 'react';
import { FlagType } from '../../../../@types/product.enum';
import Suspense from '../../../components-legacy/Suspense';
import { createBadgeStyle } from '../../../hooks/useFlagBadgeStyle';
import { useHeroContentTheme, useHeroButtonTheme } from '../../../hooks/useTheme';
import Link from '../../../Router/Link';
import Button from '../../../styled/Button';
import Hero from '../../../styled/Hero';
import { FloaterLink } from '../../../styled/Hero/Floater/wrappers';
import { ImageLink, ProductsFooter } from '../../../styled/Hero/wrappers';
import ProductSlice, { IProductSlice } from '../ProductSlice';

const Countdown = React.lazy(() => import('../../../components-legacy/Countdown'));

declare type HorizontalAlignment = 'Right' | 'Left' | 'Center';

declare type PriceStyle = 'Normal' | 'Bold';

declare type TitleSize = 'Small' | 'Medium' | 'Large' | 'ExtraLarge';

declare type TitleStyle = 'Normal' | 'Italic';

declare type Width = 'Wide' | 'Narrow';

interface IPosition {
  horizontal: number;
  vertical: number;
}

interface IGridItem {
  articleNumber: string;
  desktopPosition: IPosition;
  mobilePosition: IPosition;
  image: ISliceImage;
}

interface IProductGrid {
  link: ILink;
  backgroundColor: string;
  textColor: string;
  Items: IGridItem[];
}

interface IFloater {
  article?: {
    flags?: Array<{
      type: FlagType;
      productBanner: {
        design: {
          backgroundColor: string;
          padding: boolean;
          textColor: string;
        };
        text: string;
      };
    }>;
    id: string;
    name: string;
    price: IPrice;
    url: string;
  };
  image?: {
    url: string;
  };
  link?: {
    path?: string;
    url?: string;
  };
  horizontal: number;
  horizontalMobile: number;
  vertical: number;
  verticalMobile: number;
  imageSizeMobile: string;
  imageSizeDesktop: string;
  textColor: string;
  nameOverride?: string;
  priceOverride?: string;
  oldPriceOverride?: string;
}

export interface IHeroSlice extends ISlice {
  backgroundOpacity: number;
  buttons: ILink[];
  connectToProductGrid: boolean;
  content: { html: string; markdown: string };
  heroCountdown?: ICountdown;
  horizontalAlignment: HorizontalAlignment;
  id?: string;
  image?: ISliceImage;
  imageLoading?: 'lazy' | 'eager';
  imageQuality?: number;
  inPanel?: boolean;
  link?: ILink;
  priceStyle: PriceStyle;
  productGrid?: IProductGrid;
  productSlice: IProductSlice;
  ratio: string;
  repeatable?: IFloater[];
  showFilters: boolean;
  theme: string;
  title: string;
  titleHeadingLevel?: number;
  titleSize: TitleSize;
  titleStyle: TitleStyle;
  width: Width;
}

const HeroSlice = (props: IHeroSlice) => {
  const entireHeroLink = useMemo(
    () =>
      !props.link
        ? undefined
        : {
            url: props.link.path,
            text: props.link.title,
          },
    [props.link],
  );

  const buttons = useMemo(
    () =>
      props?.buttons?.map((b) => ({
        text: b.title,
        url: b.path,
      })),
    [props?.buttons],
  );

  const getBadgeStyle = (floater: IFloater) => {
    const design = floater.article?.flags?.find(({ type }) => type === FlagType.news)?.productBanner
      ?.design;

    if (!design) return;

    return createBadgeStyle({
      textColor: design?.textColor,
    });
  };

  const floaters = useMemo(
    () =>
      props.repeatable?.map((r) => ({
        articleNumber: r.article?.id,
        badgeLabel: r.article?.flags?.find(({ type }) => type === FlagType.news)?.productBanner
          ?.text,
        badgeStyle: getBadgeStyle(r),
        heading: r.nameOverride || r.article?.name,
        image: r.image?.url,
        imageSizeMobile: r.imageSizeMobile,
        imageSizeDesktop: r.imageSizeDesktop,
        link: r.link?.path || r.link?.url || r.article?.url,

        posLeft: r.horizontal,
        posLeftMobile: r.horizontalMobile,
        posTop: r.vertical,
        posTopMobile: r.verticalMobile,
        textColor: r.textColor,
        price: r.article?.price,
      })),
    [props.repeatable],
  );

  const contentStyle = useHeroContentTheme(props.theme);
  const buttonStyle = useHeroButtonTheme(props.theme);

  const productGridButtonStyle = useMemo(
    () =>
      props.productGrid?.backgroundColor
        ? {
            backgroundColor: props.productGrid?.backgroundColor,
            color: props.productGrid?.textColor,
          }
        : buttonStyle,
    [props.productGrid, buttonStyle],
  );

  return (
    <>
      <Hero
        buttonStyle={buttonStyle}
        content={props.content}
        contentPosition={props.horizontalAlignment}
        contentStyle={contentStyle}
        floaters={floaters}
        heading={props.title}
        headingItalic={props.titleStyle === 'Italic'}
        headingSize={props.titleSize}
        heroLink={entireHeroLink}
        image={
          props.inPanel
            ? props.image?.views?.mobile?.url
            : props.image?.views?.desktop?.url || props.image?.url
        }
        imageQuality={props.imageQuality}
        imagePlaceholderRatio={props.ratio}
        inPanel={props.inPanel}
        imageLoading={props.imageLoading}
        links={buttons}
        mobileImage={props.image?.views?.mobile?.url}
        titleHeadingLevel={props.titleHeadingLevel}
        width={props.width}
        imageWidth={props.image?.views?.mobile?.width || props.image?.width}
        imageHeight={props.image?.views?.mobile?.height || props.image?.height}
        renderActionLink={({ url, text }) => (
          <Link
            href={url}
            title={text}
            tone="soft"
            size="small"
            Component={Button}
            rounded
            as="a"
            style={buttonStyle}>
            {text}
          </Link>
        )}
        renderCountdown={
          props.heroCountdown
            ? () => (
                <Suspense fallback={null}>
                  <Countdown
                    backgroundColor={props.heroCountdown?.backgroundColor}
                    backgroundImage={props.heroCountdown?.backgroundImage?.url}
                    endedMessage={props.heroCountdown?.endedMessage}
                    endedLink={props.heroCountdown?.endLink?.path}
                    message={props.heroCountdown?.message}
                    link={props.heroCountdown?.link?.path || ''}
                    timestamp={new Date(props.heroCountdown?.date || 0)}
                  />
                </Suspense>
              )
            : undefined
        }
        renderHeroLink={({ url, text }) => <Link Component={ImageLink} href={url} title={text} />}
        renderFloaterLink={({ link, heading }, children) => (
          <Link href={link} title={heading} Component={FloaterLink}>
            {children}
          </Link>
        )}
      />

      {props.productSlice && (
        <>
          <ProductSlice {...props.productSlice} dragUp />

          {props.productGrid?.link && (
            <ProductsFooter>
              <Link
                Component={Button}
                as="a"
                href={props.productGrid.link.path}
                style={productGridButtonStyle}
                title={props.productGrid.link.title}
                tone="ghost">
                {props.productGrid.link.title}
              </Link>
            </ProductsFooter>
          )}
        </>
      )}
    </>
  );
};

export default HeroSlice;
